import { useCallback, useEffect, useMemo, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { rateRestrictions, type AvailabilityCell, type Rate, type RatePlan, type RoomType } from '@lemontaigu/pms-core';
import { useCapabilities, useCapability } from '@lemontaigu/platform-ui';
import { CAPABILITIES } from '@lemontaigu/platform';
import { usePms } from './PmsProvider.js';
import { fmtDayShort, isoAddDays, isoRange } from './logic.js';
import {
  applyBulk,
  cellKey,
  dirtyRates,
  dirtySummaryFr,
  editCell,
  fillMissing,
  fmtCents,
  newRates,
  parseCents,
  toDraft,
  WEEKDAYS_FR,
  type BulkPatch,
  type BulkScope,
  type RateDraft,
  maxBelowMin,
} from './inventaireDraft.js';

// « Inventaire » — the rate + restriction grid (R14/R15).
//
// NAMED FOR THE INDUSTRY, not for our table (operator, 2026-08-22). Channex and
// SiteMinder both call this screen « Inventory » / « Inventaire », and their
// vocabulary matches each other; ours was the odd one out. The underlying table
// stays `pms_rate` — what changed is the label a human reads, which is the half
// that has to match the two systems the desk also has open.
//
// `pms_rate` is the MASTER and is hand editable (R4); the generator proposes
// rows, this is where a human overrides them. Every rule about what an edit
// means lives in `inventaireDraft.ts` and is tested there; this file is the
// surface.
//
// ⚠ SCOPE, stated so nobody mistakes what is here for the whole feature:
//   - the three restrictions the desk actually uses are editable (rate, min
//     LOS, stop sell). CTA / CTD / max stay are R10's « columns now, push
//     unwired » and are NOT here — the columns do not exist on `pms_rate` yet.
//   - the STOP-SELL channel selector is present but confined to « tous les
//     canaux ». Per-channel stop sell is a `channel_availability_rule`, a
//     different Channex object with its own lifecycle (§14.2/§14.4), and no
//     adapter writes one yet. It is shown rather than omitted because §14.4
//     names retrofitting the selector as the one sequencing mistake left.
//   - GENERATING a grid from a parameter set is deliberately absent. It needs
//     the « hand-adjusted » marker on `pms_rate` that R4's never-overwrite
//     guard reads, and that marker does not exist yet.

const DAYS_SHOWN = 14;

/**
 * WHICH value the grid shows, one at a time — the way both vendors do it.
 *
 * SiteMinder puts this selector on every row (« Tarifs / Arrêt de vente /
 * Fermeture aux arrivées / … ») and Channex puts one at the top of the page.
 * Either way the cell shows ONE number. The first version of this screen
 * crammed price, minimum stay and stop-sell into every cell, which is unreadable
 * across fourteen columns and matches nothing the operator already knows.
 */
const MODES = [
  { key: 'rate', label: 'Tarifs' },
  { key: 'minStay', label: 'Durée minimale de séjour' },
  { key: 'closed', label: 'Arrêt de vente' },
  // CC2 (PMS-CHANNEX-CERTIFICATION-BRIEF §3.5) — SiteMinder's « Fermeture aux
  // arrivées / aux départs » and the maximum stay. Labels from GLOSSARY.
  { key: 'cta', label: 'Arrivée interdite' },
  { key: 'ctd', label: 'Départ interdit' },
  { key: 'maxStay', label: 'Séjour max.' },
] as const;
type Mode = (typeof MODES)[number]['key'];

// Visibility switches replace the one-at-a-time selector (operator, 2026-10-18):
// each plan shows one row per switched-on layer. Only the price is on at first.
const DEFAULT_LAYERS: Record<Mode, boolean> = {
  rate: true,
  minStay: false,
  closed: false,
  cta: false,
  ctd: false,
  maxStay: false,
};

// Room-type colour code — one hue per category, used for the band's stripe and
// tint. Unknown codes fall back to grey.
const CATEGORY_HUE: Record<string, string> = {
  DSTD: '#4a7fb5',
  DSUP: '#2f9e8f',
  TSTD: '#c98a4b',
  TSUP: '#b5654a',
  FAM: '#7a5fa8',
  FAMS: '#a8567f',
};
const categoryHue = (code: string) => CATEGORY_HUE[code] ?? '#8a8577';

/** Availability as a heat colour: green (plenty) → amber → red (none/over). */
function availabilityFill(available: number, total: number): string {
  if (total <= 0) return 'transparent';
  const frac = Math.max(0, Math.min(1, available / total));
  return `hsl(${Math.round(frac * 125)} 60% 82%)`;
}

// Order of the bands: the room categories as the hotel lists them, and the
// price plans from the base rate up through half board. Unknown codes sort last.
const CATEGORY_ORDER = ['DSTD', 'DSUP', 'TSTD', 'TSUP', 'FAM', 'FAMS'];
const BOARD_ORDER = ['none', 'bb', 'hb'];

const rank = (order: readonly string[], value: string) => {
  const i = order.indexOf(value);
  return i === -1 ? order.length : i;
};

export function Inventaire() {
  const pms = usePms();
  // The L2 gate reads the actor's capabilities, and they resolve
  // ASYNCHRONOUSLY: until the first fetch settles the actor is ANONYMOUS and
  // every call is denied (the fail-closed loading state). `PmsProvider` gates
  // its children on the PROPERTY list, which is a different fetch — so on a
  // cold load this tab can mount, read, and be refused before its permissions
  // have arrived. Found by reloading the page: the grid came back
  // « Vous n'avez pas l'autorisation » and stayed that way, because nothing
  // retried. Waiting for `capsLoading` is the fix; re-reading when it settles
  // is what makes it recover rather than merely delay.
  const { loading: capsLoading } = useCapabilities();
  const todayIso = useMemo(() => todayIsoParis(), []);
  const [startIso, setStartIso] = useState(todayIso);
  const [plans, setPlans] = useState<RatePlan[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [avail, setAvail] = useState<AvailabilityCell[]>([]);
  const [original, setOriginal] = useState<RateDraft>(() => new Map());
  const [draft, setDraft] = useState<RateDraft>(() => new Map());
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [layers, setLayers] = useState<Record<Mode, boolean>>(DEFAULT_LAYERS);
  const shownModes = MODES.filter((m) => layers[m.key]);
  // Reads need pms.rate.read (desk); every edit needs pms.rate.write (bos@ only).
  const canEdit = useCapability(CAPABILITIES.PMS_RATE_WRITE);
  // Channel plans (OTA) are hidden by default: they inherit the parent's price,
  // so they only add rows to read. The toggle shows them when needed.
  const [showOta, setShowOta] = useState(false);
  const visiblePlans = useMemo(
    () => plans.filter((p) => showOta || p.derivesFromId === null),
    [plans, showOta],
  );

  const days = useMemo(() => isoRange(startIso, DAYS_SHOWN), [startIso]);
  const endIso = useMemo(() => isoAddDays(startIso, DAYS_SHOWN - 1), [startIso]);

  const load = useCallback(async () => {
    if (capsLoading) return;
    setErr('');
    setNotice('');
    const [gr, pl, ty, av] = await Promise.all([
      pms.rates.grid(startIso, endIso),
      pms.reference.ratePlans(),
      pms.reference.roomTypes(),
      // Availability is DERIVED and read-only here. It sits in this screen
      // because that is where both vendors put it and where it is actually
      // read — a price is decided against the number of rooms left, and making
      // the operator hold two tabs open to see them together is the whole
      // ergonomic point of the band layout below.
      pms.availability.calendar(startIso, endIso),
    ]);
    const bad = [gr, pl, ty, av].find((r) => !r.ok);
    if (bad && !bad.ok) {
      // Fails loudly rather than degrading to an empty grid: an empty rate grid
      // and a grid that failed to load look identical, and one of them means
      // « everything is free ».
      setErr(bad.error.message);
      return;
    }
    if (gr.ok) {
      setOriginal(toDraft(gr.value));
      setDraft(toDraft(gr.value));
    }
    // Distributed plans first: what reaches a channel is where a mistake is
    // expensive (0108). Today that is all of them.
    // Parents (plans that hold their own price) before the channel plans that
    // inherit it, and the boards in the order of the price list.
    if (pl.ok)
      setPlans(
        [...pl.value].sort(
          (a, b) =>
            rank(BOARD_ORDER, a.board) - rank(BOARD_ORDER, b.board) ||
            Number(a.derivesFromId !== null) - Number(b.derivesFromId !== null) ||
            a.code.localeCompare(b.code),
        ),
      );
    if (ty.ok)
      setRoomTypes(
        [...ty.value].sort((a, b) => rank(CATEGORY_ORDER, a.code) - rank(CATEGORY_ORDER, b.code) || a.code.localeCompare(b.code)),
      );
    if (av.ok) setAvail(av.value);
  }, [pms, startIso, endIso, capsLoading]);

  useEffect(() => {
    void load();
  }, [load]);

  // Two kinds of pending change, and they are different facts: rows that
  // EXISTED and were edited, and rows that did not exist and were created by a
  // fill. `dirtyRates` only sees the first — without `newRates` a freshly
  // filled window would report « aucune modification » and save nothing.
  const dirty = useMemo(() => dirtyRates(original, draft), [original, draft]);
  const created = useMemo(() => newRates(original, draft), [original, draft]);
  const pending = useMemo(() => [...dirty, ...created], [dirty, created]);

  const save = useCallback(async () => {
    setSaving(true);
    setErr('');
    setNotice('');
    // Only what changed. A blanket upsert of the window would rewrite rows
    // nobody touched — see `dirtyRates`.
    const r = await pms.rates.save(pending);
    setSaving(false);
    if (!r.ok) {
      setErr(r.error.message);
      return;
    }
    // Says what actually happened, and no more. Saving a rate ENQUEUES an ARI
    // push (0112, a trigger on `pms_rate` — so it happens whatever writes the
    // row, not only this screen). It does not perform one: the drain is the
    // job tick, which stays dormant until go-live. « Envoyé au canal » would be
    // a claim this screen cannot keep.
    const what =
      created.length === 0
        ? dirtySummaryFr(dirty.length)
        : `${dirtySummaryFr(dirty.length)}, ${created.length} tarif(s) créé(s)`;
    setNotice(`${what} — enregistré. Mise à jour du canal mise en file d’attente.`);
    setOriginal(new Map(draft));
  }, [pms, pending, dirty, created, draft]);

  const onCell = (key: string, patch: BulkPatch) => setDraft((d) => editCell(d, key, patch));

  const availBy = useMemo(
    () => new Map(avail.map((c) => [`${c.roomTypeId}|${c.day}`, c] as const)),
    [avail],
  );

  return (
    <div className="pms">
      <div className="pms-head">
        <h2 className="section-title">Inventaire — tarifs et restrictions</h2>
        <span className="pms-hint">
          `pms_rate` est le maître, éditable à la main. « Enregistrer » met la mise à jour du canal en
          file d’attente ; l’envoi lui-même est effectué par la tâche de fond.
        </span>
      </div>

      <div className="pms-toolbar">
          <button type="button" onClick={() => setStartIso(isoAddDays(startIso, -DAYS_SHOWN))}>
            ← {DAYS_SHOWN} jours
          </button>
          <input
            type="date"
            value={startIso}
            onChange={(e) => setStartIso(e.target.value)}
            aria-label="Début de la période"
          />
          <button type="button" onClick={() => setStartIso(isoAddDays(startIso, DAYS_SHOWN))}>
            {DAYS_SHOWN} jours →
          </button>
          <fieldset className="pms-chips">
            <legend>Afficher</legend>
            {MODES.map((m) => (
              <label key={m.key}>
                <input
                  type="checkbox"
                  role="switch"
                  checked={layers[m.key]}
                  onChange={(e) => setLayers((l) => ({ ...l, [m.key]: e.target.checked }))}
                />{' '}
                {m.label}
              </label>
            ))}
          </fieldset>
          <label>
            <input type="checkbox" checked={showOta} onChange={(e) => setShowOta(e.target.checked)} /> Afficher les OTA
          </label>
          {canEdit && (
            <>
              <button type="button" onClick={() => setShowBulk((v) => !v)}>
                {showBulk ? 'Fermer la mise à jour groupée' : 'Mise à jour groupée'}
              </button>
              <button type="button" onClick={() => void save()} disabled={pending.length === 0 || saving}>
                {saving ? 'Enregistrement…' : `Enregistrer (${pending.length})`}
              </button>
            </>
          )}
        </div>

      {err !== '' && <p className="auth-err">{err}</p>}
      {notice !== '' && <p className="pms-warn">{notice}</p>}
      <p className="pms-hint">
        {dirtySummaryFr(dirty.length)}
        {created.length > 0 && ` · ${created.length} tarif(s) à créer`}
      </p>

      {canEdit && showBulk && (
        <BulkPanel
          plans={plans}
          roomTypes={roomTypes}
          from={startIso}
          to={endIso}
          onApply={(scope, patch) => {
            setDraft((d) => applyBulk(d, scope, patch));
            setShowBulk(false);
          }}
          onFill={(scope, seed) => {
            setDraft((d) => fillMissing(d, scope, days, seed));
            setShowBulk(false);
          }}
        />
      )}

      {plans.length === 0 || roomTypes.length === 0 ? (
        <p className="pms-hint">Aucun plan tarifaire ou aucune catégorie configurée.</p>
      ) : (
        <div className="pms-cal-scroll">
          <table className="pms-cal pms-inv">
            <thead>
              <tr>
                <th scope="col">Catégorie / plan</th>
                <th scope="col" className="pms-inv-what" />
                {days.map((d) => (
                  <th key={d} scope="col">
                    {fmtDayShort(d)}
                  </th>
                ))}
              </tr>
            </thead>
            {/* ONE BAND PER CATEGORY — the SiteMinder / Channex shape (operator,
                2026-08-22). The category leads, its availability sits directly
                under the header, and the rate plans hang beneath it. The first
                version was plan-major, which put « STD · DSTD » and « STD · DSUP »
                on adjacent rows and buried the thing a price is actually decided
                against: how many rooms of that category are left. */}
            {roomTypes.map((rt) => (
              <tbody
                key={rt.id}
                className="pms-inv-band"
                style={{ '--cat': categoryHue(rt.code) } as React.CSSProperties}
              >
                <tr className="pms-inv-cat">
                  <th scope="row">{rt.labelFr}</th>
                  <td className="pms-inv-what">DISPONIBILITÉ</td>
                  {days.map((d) => {
                    const cell = availBy.get(`${rt.id}|${d}`);
                    return (
                      <td
                        key={d}
                        className={cell !== undefined && cell.available <= 0 ? 'pms-inv-avl is-zero' : 'pms-inv-avl'}
                        style={cell === undefined ? undefined : { background: availabilityFill(cell.available, cell.total) }}
                        title={cell === undefined ? undefined : `${cell.available} / ${cell.total} disponible(s)`}
                      >
                        {/* Derived, never editable: availability is rooms minus
                            reservations minus blocks, and a typed-over number
                            would be a lie the moment anything books. */}
                        {cell === undefined ? '·' : cell.available}
                      </td>
                    );
                  })}
                </tr>
                {visiblePlans.map((plan) =>
                  shownModes.map((m, mi) => (
                  <tr key={`${plan.id}|${m.key}`} className={mi === 0 ? 'pms-inv-first' : undefined}>
                    {mi === 0 && (
                    <th scope="row" className="pms-inv-plan" rowSpan={shownModes.length}>
                      {plan.labelFr}
                      {!plan.distributed && (
                        <span className="pms-chip" title="Plan non distribué — jamais envoyé au canal">
                          direct
                        </span>
                      )}
                      {plan.channelCode !== null && (
                        <span className="pms-chip" title={`Plan propre au canal ${plan.channelCode}`}>
                          {plan.channelCode}
                        </span>
                      )}
                    </th>
                    )}
                    <td className="pms-inv-what">{m.label}</td>
                    {days.map((d) => {
                      const key = cellKey(plan.id, rt.id, d);
                      const cell = draft.get(key);
                      if (cell === undefined) {
                        // No row means no price. Shown as « — » and not as 0:
                        // an unpriced day and a free night are different facts.
                        return (
                          <td key={d} className="pms-cal-cell" title="Aucun tarif pour ce jour">
                            —
                          </td>
                        );
                      }
                      // A CHANNEL plan does not hold a price — it inherits its
                      // parent's on Channex (R15/0110). Showing an editable
                      // price here invites typing one that goes nowhere, which
                      // is exactly what happened: 62 such values were stored
                      // and silently ignored before this was noticed on a
                      // read-back. The parent's figure is shown instead, and it
                      // is not editable.
                      const inherited =
                        plan.derivesFromId === null
                          ? null
                          : (draft.get(cellKey(plan.derivesFromId, rt.id, d))?.priceCents ?? null);
                      return (
                        <RateCell
                          key={d}
                          rate={cell}
                          mode={m.key}
                          readOnly={!canEdit}
                          inheritedCents={inherited}
                          onChange={(p) => onCell(key, p)}
                        />
                      );
                    })}
                  </tr>
                  )),
                )}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}

function RateCell({
  rate,
  mode,
  readOnly,
  inheritedCents,
  onChange,
}: {
  rate: Rate;
  mode: Mode;
  readOnly: boolean;
  /** Set when this row is a channel plan: the parent's price, which Channex
   *  will apply. Null for a parent, which holds its own. */
  inheritedCents: number | null;
  onChange: (p: BulkPatch) => void;
}) {
  // ONE value, chosen by the display selector — the vendors' shape. A cell that
  // shows three things at once is unreadable across fourteen columns.
  if (mode === 'closed') {
    return (
      <td className={rate.closed ? 'pms-cal-cell is-closed' : 'pms-cal-cell'}>
        <input
          type="checkbox"
          checked={rate.closed}
          disabled={readOnly}
          onChange={(e) => onChange({ closed: e.target.checked })}
          aria-label={`Arrêt de vente le ${rate.date}`}
        />
      </td>
    );
  }

  if (mode === 'cta' || mode === 'ctd') {
    const field = mode === 'cta' ? 'closedToArrival' : 'closedToDeparture';
    const on = rateRestrictions(rate)[field];
    return (
      <td className={on ? 'pms-cal-cell is-closed' : 'pms-cal-cell'}>
        <input
          type="checkbox"
          checked={on}
          disabled={readOnly}
          onChange={(e) => onChange({ [field]: e.target.checked })}
          aria-label={`${mode === 'cta' ? 'Arrivée interdite' : 'Départ interdit'} le ${rate.date}`}
        />
      </td>
    );
  }

  if (mode === 'maxStay') {
    const max = rateRestrictions(rate).maxStay;
    const broken = maxBelowMin(rate);
    return (
      <td
        className={broken ? 'pms-cal-cell is-closed' : 'pms-cal-cell'}
        title={broken ? `Séjour max. (${max}) inférieur à la durée minimale (${rate.minStay}) : date invendable` : undefined}
      >
        <input
          type="number"
          min={1}
          className="pms-rate-input"
          value={max ?? ''}
          placeholder="—"
          disabled={readOnly}
          onChange={(e) => {
            const v = e.target.value.trim();
            const n = Number(v);
            onChange({ maxStay: v === '' || !Number.isFinite(n) || n < 1 ? null : Math.round(n) });
          }}
          aria-label={`Séjour maximum du ${rate.date}`}
        />
      </td>
    );
  }

  if (mode === 'minStay') {
    return (
      <td className={rate.closed ? 'pms-cal-cell is-closed' : 'pms-cal-cell'}>
        <input
          type="number"
          min={1}
          className="pms-rate-input"
          value={rate.minStay}
          disabled={readOnly}
          onChange={(e) => onChange({ minStay: Math.max(1, Number(e.target.value) || 1) })}
          aria-label={`Durée minimale du ${rate.date}`}
        />
      </td>
    );
  }

  // The restrictions ARE this row's to carry — min stay and stop sell are the
  // whole reason a channel plan exists — so only the price is read-only.
  if (inheritedCents !== null) {
    return (
      <td
        className={rate.closed ? 'pms-cal-cell is-closed pms-cell-inherited' : 'pms-cal-cell pms-cell-inherited'}
        title="Tarif hérité du plan parent — un plan de canal ne porte pas de prix"
      >
        {fmtCents(inheritedCents)}
      </td>
    );
  }

  return <PriceCell rate={rate} readOnly={readOnly} onChange={onChange} />;
}

function PriceCell({ rate, readOnly, onChange }: { rate: Rate; readOnly: boolean; onChange: (p: BulkPatch) => void }) {
  const [text, setText] = useState(fmtCents(rate.priceCents));
  const [bad, setBad] = useState(false);

  // The cell follows the draft when a bulk update rewrites it underneath.
  useEffect(() => {
    setText(fmtCents(rate.priceCents));
    setBad(false);
  }, [rate.priceCents]);

  const commit = () => {
    const cents = parseCents(text);
    if (cents === null) {
      // A typo leaves the price alone and says so, rather than zeroing it.
      setBad(true);
      setText(fmtCents(rate.priceCents));
      return;
    }
    setBad(false);
    onChange({ priceCents: cents });
  };

  return (
    <td className={rate.closed ? 'pms-cal-cell is-closed' : 'pms-cal-cell'}>
      <input
        className={bad ? 'pms-rate-input is-bad' : 'pms-rate-input'}
        value={text}
        disabled={readOnly}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        aria-label={`Tarif du ${rate.date}`}
        title={rate.closed ? 'Fermé à la vente' : undefined}
      />
    </td>
  );
}

function BulkPanel({
  plans,
  roomTypes,
  from,
  to,
  onApply,
  onFill,
}: {
  plans: RatePlan[];
  roomTypes: RoomType[];
  from: string;
  to: string;
  onApply: (scope: BulkScope, patch: BulkPatch) => void;
  onFill: (scope: BulkScope, seed: { priceCents: number; minStay: number; closed: boolean }) => void;
}) {
  const [f, setF] = useState(from);
  const [t, setT] = useState(to);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [planIds, setPlanIds] = useState<number[]>(plans.map((p) => p.id));
  const [typeIds, setTypeIds] = useState<number[]>(roomTypes.map((r) => r.id));
  const [price, setPrice] = useState('');
  const [minStay, setMinStay] = useState('');
  const [closed, setClosed] = useState<'' | 'open' | 'closed'>('');
  const [cta, setCta] = useState<'' | 'on' | 'off'>('');
  const [ctd, setCtd] = useState<'' | 'on' | 'off'>('');
  const [maxStay, setMaxStay] = useState('');

  const toggle = (list: number[], id: number, set: (v: number[]) => void) =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const apply = () => {
    const patch: BulkPatch = {};
    const cents = parseCents(price);
    if (cents !== null) patch.priceCents = cents;
    const ms = Number(minStay);
    if (minStay.trim() !== '' && Number.isFinite(ms) && ms >= 1) patch.minStay = Math.round(ms);
    if (closed !== '') patch.closed = closed === 'closed';
    if (cta !== '') patch.closedToArrival = cta === 'on';
    if (ctd !== '') patch.closedToDeparture = ctd === 'on';
    // « aucun » lifts the maximum; a number sets it; empty leaves it alone.
    if (maxStay.trim().toLowerCase() === 'aucun') patch.maxStay = null;
    else {
      const mx = Number(maxStay);
      if (maxStay.trim() !== '' && Number.isFinite(mx) && mx >= 1) patch.maxStay = Math.round(mx);
    }
    if (Object.keys(patch).length === 0) return;
    onApply({ from: f, to: t, weekdays, ratePlanIds: planIds, roomTypeIds: typeIds }, patch);
  };

  const fill = () => {
    const cents = parseCents(price);
    if (cents === null) return; // a fill needs a price; there is nothing to seed from otherwise
    const ms = Number(minStay);
    onFill(
      { from: f, to: t, weekdays, ratePlanIds: planIds, roomTypeIds: typeIds },
      {
        priceCents: cents,
        minStay: minStay.trim() !== '' && Number.isFinite(ms) && ms >= 1 ? Math.round(ms) : 1,
        closed: closed === 'closed',
      },
    );
  };

  return (
    <div className="pms-form">
      <div className="pms-toolbar">
        <label>
          Du <input type="date" value={f} onChange={(e) => setF(e.target.value)} />
        </label>
        <label>
          au <input type="date" value={t} onChange={(e) => setT(e.target.value)} />
        </label>
        <fieldset className="pms-chips">
          <legend>Jours</legend>
          {WEEKDAYS_FR.map((d, i) => (
            <label key={d}>
              <input
                type="checkbox"
                checked={weekdays.includes(i)}
                onChange={() => toggle(weekdays, i, setWeekdays)}
              />
              {d}
            </label>
          ))}
          <span className="pms-hint">aucun coché = tous les jours</span>
        </fieldset>
      </div>

      <div className="pms-toolbar">
        <fieldset>
          <legend>Plans</legend>
          {plans.map((p) => (
            <label key={p.id}>
              <input
                type="checkbox"
                checked={planIds.includes(p.id)}
                onChange={() => toggle(planIds, p.id, setPlanIds)}
              />
              {p.code}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>Catégories</legend>
          {roomTypes.map((r) => (
            <label key={r.id}>
              <input
                type="checkbox"
                checked={typeIds.includes(r.id)}
                onChange={() => toggle(typeIds, r.id, setTypeIds)}
              />
              {r.code}
            </label>
          ))}
        </fieldset>
      </div>

      <div className="pms-toolbar">
        <label>
          Tarif <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="101,00" />
        </label>
        <label>
          Durée min.{' '}
          <input
            type="number"
            min={1}
            value={minStay}
            onChange={(e) => setMinStay(e.target.value)}
            placeholder="—"
          />
        </label>
        <label>
          Vente{' '}
          <select value={closed} onChange={(e) => setClosed(e.target.value as '' | 'open' | 'closed')}>
            <option value="">(inchangé)</option>
            <option value="open">Ouverte</option>
            <option value="closed">Fermée</option>
          </select>
        </label>
        <label>
          Arrivée{' '}
          <select value={cta} onChange={(e) => setCta(e.target.value as '' | 'on' | 'off')}>
            <option value="">(inchangé)</option>
            <option value="off">Autorisée</option>
            <option value="on">Interdite</option>
          </select>
        </label>
        <label>
          Départ{' '}
          <select value={ctd} onChange={(e) => setCtd(e.target.value as '' | 'on' | 'off')}>
            <option value="">(inchangé)</option>
            <option value="off">Autorisé</option>
            <option value="on">Interdit</option>
          </select>
        </label>
        <label title="Un nombre de nuits, ou « aucun » pour lever le maximum">
          Séjour max.{' '}
          <input value={maxStay} onChange={(e) => setMaxStay(e.target.value)} placeholder="—" size={6} />
        </label>
        {/* §14.4 — the selector is here from the start on purpose. Channex
            scopes a stop sell per channel through `channel_availability_rule`,
            a different object with its own lifecycle, and nothing writes one
            yet. Designing the panel channel-blind and retrofitting this is the
            one sequencing mistake the brief says is still available. */}
        <label title="La fermeture par canal passe par une règle Channex distincte — pas encore disponible">
          Canal{' '}
          <select value="all" disabled>
            <option value="all">Tous les canaux</option>
          </select>
        </label>
        <button type="button" onClick={apply}>
          Appliquer
        </button>
        {/* A SEPARATE act, not a checkbox on « Appliquer ». Updating edits a
            position that exists; creating decides that dates which had no price
            now have one. Filling never overwrites, so it is safe to repeat. */}
        <button type="button" onClick={fill} title="Ne touche jamais un tarif existant">
          Créer les tarifs manquants
        </button>
      </div>
      <p className="pms-hint">
        « Appliquer » ne modifie que les jours déjà tarifés. « Créer les tarifs manquants » ajoute
        les jours absents de la période affichée sans jamais écraser un tarif existant. Les plans de
        canal ne portent pas de prix : ils héritent de celui du plan parent, et seules leurs
        restrictions leur appartiennent.
      </p>
    </div>
  );
}
